<template>
  <div class="groups-management-container">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">{{ $t('groupsManagement.title') }}</h2>
      <button class="btn-primary" @click="showAddGroupForm = true">
        <i class="fas fa-plus"></i> {{ $t('groupsManagement.addGroup') }}
      </button>
    </div>

    <!-- Search Bar -->
    <div class="search-container">
      <i class="fas fa-search search-icon"></i>
      <input 
        type="text" 
        class="search-input" 
        :placeholder="$t('groupsManagement.searchGroups')" 
        v-model="searchQuery"
        @keyup.enter="searchGroups"
      />
      <button class="btn-search" @click="searchGroups">
        {{ $t('groupsManagement.search') }}
      </button>
    </div>

    <div class="table-container" v-if="groups.length">
      <table class="data-table">
        <thead>
          <tr>
            <th @click="setSort('group_name')" class="sortable">
              {{ $t('groupsManagement.groupName') }}
              <i v-if="sortBy === 'group_name'" 
                :class="['sort-icon', sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down']">
              </i>
              <i v-else class="sort-icon fas fa-sort"></i>
            </th>
            <th @click="setSort('teacher_name')" class="sortable">
              {{ $t('groupsManagement.teacherName') }}
              <i v-if="sortBy === 'teacher_name'" 
                :class="['sort-icon', sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down']">
              </i>
              <i v-else class="sort-icon fas fa-sort"></i>
            </th>
            <th>{{ $t('groupsManagement.studentsList') }}</th>
            <th>{{ $t('groupsManagement.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in groups" :key="group.id">
            <td class="group-name-cell">{{ group.group_name }}</td>
            <td class="teacher-name-cell">{{ group.teacher_name }}</td>
            <td class="students-cell">
              <div v-if="group.students && group.students.length" class="students-list">
                <div v-for="student in group.students" :key="student.id" class="student-item">
                  {{ student.name }}
                </div>
              </div>
              <div v-else class="no-students">{{ $t('groupsManagement.noStudents') }}</div>
            </td>
            <td class="actions-cell">
              <button class="btn-icon edit" @click="startEditGroup(group)" title="Edit">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn-icon delete" @click="confirmDeleteGroup(group)" title="Delete">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="empty-state" v-else>
      <i class="fas fa-users-slash empty-icon"></i>
      <p>{{ $t('groupsManagement.noGroupsFound') }}</p>
    </div>

    <div class="pagination-controls">
      <div class="page-size-selector">
        <label>{{ $t('groupsManagement.rowsPerPage') }}:</label>
        <select v-model="pageSize" @change="onPageSizeChange">
          <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
        </select>
      </div>
      <div class="pagination-nav">
        <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-secondary">
          {{ $t('groupsManagement.previous') }}
        </button>
        <span class="pagination-info">{{ $t('groupsManagement.page') }} {{ currentPage }}</span>
        <button @click="nextPage" :disabled="groups.length < pageSize" class="btn btn-secondary">
          {{ $t('groupsManagement.next') }}
        </button>
      </div>
    </div>

    <!-- Toast Notification -->
    <div 
      class="toast-notification" 
      :class="{ show: notificationVisible, success: notificationType === 'success', error: notificationType === 'error' }"
      v-if="notificationVisible"
    >
      <div class="toast-icon">
        <i :class="notificationType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
      </div>
      <div class="toast-content">
        {{ notificationMessage }}
      </div>
    </div>

    <!-- Add Group Form -->
    <div class="modal-overlay" v-if="showAddGroupForm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('groupsManagement.addNewGroup') }}</h3>
          <button class="close-button" @click="closeAddGroupForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="addGroup" class="modal-form">
          <div class="form-group">
            <label for="new-group-name">{{ $t('groupsManagement.groupName') }}</label>
            <input 
              id="new-group-name"
              type="text" 
              class="form-control" 
              v-model="newGroup.group_name" 
              required
            >
          </div>
          
          <div class="form-group">
            <label for="new-teacher">{{ $t('groupsManagement.teacher') }}</label>
            <div class="search-input-wrapper">
              <input 
                id="new-teacher"
                type="text" 
                class="form-control" 
                v-model="newGroup.teacherName" 
                @input="fetchTeachers()" 
                required
              >
              <div v-if="teacherError" class="error-message">
                {{ $t('groupsManagement.noTeacherFound') }}
              </div>
              <div v-if="teacherSuggestions.length > 0" class="dropdown-suggestions">
                <div v-for="teacher in teacherSuggestions" 
                     :key="teacher.id" 
                     class="suggestion-item"
                     @click="selectTeacher(teacher)">
                  {{ teacher.name }}
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label for="new-students">{{ $t('groupsManagement.students') }}</label>
            <div class="selected-items">
              <div v-for="student in newGroup.students" 
                   :key="student.id"
                   class="selected-item">
                <span>{{ student.name }}</span>
                <button 
                  type="button" 
                  class="remove-button" 
                  @click="removeStudent(student.id)"
                  title="Remove student"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            <div class="search-input-wrapper">
              <div class="add-student-input">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="newStudentName" 
                  @input="fetchStudents()" 
                  :placeholder="$t('groupsManagement.addStudent')"
                >
                <button 
                  type="button" 
                  class="btn-secondary" 
                  @click="addStudent"
                  :disabled="!selectedNewStudent"
                >
                  <i class="fas fa-plus"></i>
                  {{ $t('groupsManagement.addStudent') }}
                </button>
              </div>
              
              <div v-if="studentError" class="error-message">
                {{ $t('groupsManagement.noStudentFound') }}
              </div>
              
              <div v-if="studentSuggestions.length > 0" class="dropdown-suggestions">
                <div v-for="student in studentSuggestions" 
                     :key="student.id" 
                     class="suggestion-item"
                     @click="selectStudent(student)">
                  {{ student.name }}
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="closeAddGroupForm">
              {{ $t('groupsManagement.cancel') }}
            </button>
            <button type="submit" class="btn-primary">
              {{ $t('groupsManagement.addGroup') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Group Form -->
    <div class="modal-overlay" v-if="showEditGroupForm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('groupsManagement.editGroup') }}</h3>
          <button class="close-button" @click="closeEditGroupForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="updateGroup" class="modal-form">
          <div class="form-group">
            <label for="edit-group-name">{{ $t('groupsManagement.groupName') }}</label>
            <input 
              id="edit-group-name"
              type="text" 
              class="form-control" 
              v-model="editGroup.group_name" 
              required
            >
          </div>
          
          <div class="form-group">
            <label for="edit-teacher">{{ $t('groupsManagement.teacher') }}</label>
            <div class="search-input-wrapper">
              <input 
                id="edit-teacher"
                type="text" 
                class="form-control" 
                v-model="editGroup.teacherName" 
                @input="fetchTeachers(true)" 
                required
              >
              <div v-if="teacherError" class="error-message">
                {{ $t('groupsManagement.noTeacherFound') }}
              </div>
              <div v-if="teacherSuggestions.length > 0" class="dropdown-suggestions">
                <div v-for="teacher in teacherSuggestions" 
                     :key="teacher.id" 
                     class="suggestion-item"
                     @click="selectTeacher(teacher, true)">
                  {{ teacher.name }}
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label for="edit-students">{{ $t('groupsManagement.students') }}</label>
            <div class="selected-items">
              <div v-for="student in editGroup.students || []" 
                   :key="student.id"
                   class="selected-item">
                <span>{{ student.name }}</span>
                <button 
                  type="button" 
                  class="remove-button" 
                  @click="removeStudentFromEdit(student.id)"
                  title="Remove student"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            <div class="search-input-wrapper">
              <div class="add-student-input">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="editStudentName" 
                  @input="fetchStudents(true)" 
                  :placeholder="$t('groupsManagement.addStudent')"
                >
                <button 
                  type="button" 
                  class="btn-secondary" 
                  @click="addStudentToEdit"
                  :disabled="!selectedEditStudent"
                >
                  <i class="fas fa-plus"></i>
                  {{ $t('groupsManagement.addStudent') }}
                </button>
              </div>
              
              <div v-if="studentError" class="error-message">
                {{ $t('groupsManagement.noStudentFound') }}
              </div>
              
              <div v-if="studentSuggestions.length > 0" class="dropdown-suggestions">
                <div v-for="student in studentSuggestions" 
                     :key="student.id" 
                     class="suggestion-item"
                     @click="selectStudent(student, true)">
                  {{ student.name }}
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="closeEditGroupForm">
              {{ $t('groupsManagement.cancel') }}
            </button>
            <button type="submit" class="btn-primary">
              {{ $t('groupsManagement.updateGroup') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal-overlay" v-if="showDeleteConfirmation">
      <div class="modal-content confirmation-modal">
        <div class="modal-header">
          <h3>{{ $t('groupsManagement.confirmDelete') }}</h3>
          <button class="close-button" @click="cancelDelete">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="confirmation-content">
          <p>{{ $t('groupsManagement.deleteConfirmMessage') }} <strong>{{ groupToDelete?.group_name }}</strong>?</p>
          <p class="warning-text">{{ $t('groupsManagement.deleteWarning') }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelDelete">
            {{ $t('groupsManagement.cancel') }}
          </button>
          <button class="btn-danger" @click="deleteConfirmed">
            {{ $t('groupsManagement.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../router/axiosSetup';

export default {
  data() {
    return {
      searchQuery: '',
      groups: [],
      teachers: [],
      showAddGroupForm: false,
      showEditGroupForm: false,
      showDeleteConfirmation: false,
      groupToDelete: null,
      newGroup: {
        group_name: '',
        teacher_id: '',
        teacherName: '',
        students: []
      },
      editGroup: {
        id: null,
        group_name: '',
        teacher_id: '',
        teacherName: '',
        students: []
      },
      notificationVisible: false,
      notificationType: 'success', // Add this property
      notificationMessage: '',
      currentPage: 1,
      pageSize: 10,
      totalGroups: 0,
      totalPages: 1,
      sortBy: 'group_name',
      sortOrder: 'asc',
      teacherSuggestions: [],
      teacherError: false,
      studentSuggestions: [],
      studentError: false,
      newStudentName: '',
      editStudentName: '',
      selectedNewStudent: null,
      selectedEditStudent: null,
    };
  },
  methods: {
    async searchGroups() {
      this.isLoading = true;
      try {
        const response = await axiosInstance.get('/admin/search-groups', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: {
            query: this.searchQuery,
            limit: this.pageSize,
            offset: (this.currentPage - 1) * this.pageSize,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder
          }
        });
        this.groups = response.data;
        this.isLoading = false;
      } catch (error) {
        console.error('Error searching groups:', error);
        this.isLoading = false;
      }
    },
    
    async addGroup() {
      try {
        await axiosInstance.post('/admin/add-group', this.newGroup, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Show success notification
        this.showNotification('success', this.$t('groupsManagement.groupCreated'));
        
        // Refresh the groups list
        this.searchGroups();
        this.resetNewGroup();
        this.showAddGroupForm = false;
      } catch (error) {
        console.error('Error adding group:', error);
        // Show error notification
        this.showNotification('error', this.$t('groupsManagement.errorAddingGroup'));
      }
    },
    
    async updateGroup() {
      try {
        await axiosInstance.put('/admin/update-group', this.editGroup, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Show success notification
        this.showNotification('success', this.$t('groupsManagement.groupUpdated'));
        
        // Refresh the groups list
        this.searchGroups();
        this.resetEditGroup();
        this.showEditGroupForm = false;
      } catch (error) {
        console.error('Error updating group:', error);
        // Show error notification
        this.showNotification('error', this.$t('groupsManagement.errorUpdatingGroup'));
      }
    },
    
    confirmDeleteGroup(group) {
      this.groupToDelete = group;
      this.showDeleteConfirmation = true;
    },
    
    cancelDelete() {
      this.groupToDelete = null;
      this.showDeleteConfirmation = false;
    },
    
    async deleteConfirmed() {
      if (!this.groupToDelete) return;
      
      try {
        await axiosInstance.delete('/admin/delete-group', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          data: { id: this.groupToDelete.id }
        });
        
        // Show success notification
        this.showNotification('success', this.$t('groupsManagement.groupDeleted'));
        
        // Refresh the groups list
        this.searchGroups();
        this.showDeleteConfirmation = false;
        this.groupToDelete = null;
      } catch (error) {
        console.error('Error deleting group:', error);
        // Show error notification
        this.showNotification('error', this.$t('groupsManagement.errorDeletingGroup'));
      }
    },
    
    setSort(column) {
      if (this.sortBy === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortOrder = 'asc';
      }
      this.searchGroups();
    },
    
    onPageSizeChange() {
      this.currentPage = 1; // Reset to first page when changing page size
      this.searchGroups();
    },
    
    firstPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        this.searchGroups();
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchGroups();
      }
    },
    
    nextPage() {
      if (this.groups.length === this.pageSize) { // Assume there's a next page if we received a full page
        this.currentPage++;
        this.searchGroups();
      }
    },
    
    lastPage() {
      if (this.totalPages && this.currentPage !== this.totalPages) {
        this.currentPage = this.totalPages;
        this.searchGroups();
      } else {
        // If totalPages is not available, try to load one more page
        this.nextPage();
      }
    },
    
    resetNewGroup() {
      this.newGroup = {
        group_name: '',
        teacher_id: '',
        teacherName: '',
        students: []
      };
      this.newStudentName = '';
      this.selectedNewStudent = null;
    },
    
    resetEditGroup() {
      this.editGroup = {
        id: null,
        group_name: '',
        teacher_id: '',
        teacherName: '',
        students: []
      };
      this.editStudentName = '';
      this.selectedEditStudent = null;
    },
    
    async startEditGroup(group) {
      this.editGroup = { ...group };

      // Fetch teacher_id if not present in the group object
      if (!group.teacher_id) {
        try {
          const response = await axiosInstance.get(`/admin/group/${group.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          const groupData = response.data;
          this.editGroup.teacher_id = groupData.teacher_id;
          this.editGroup.teacherName = groupData.teacher_name;
        } catch (error) {
          console.error('Error fetching group details:', error);
          this.showNotification('error', this.$t('groupsManagement.errorFetchingGroupDetails'));
        }
      } else {
        this.editGroup.teacherName = group.teacher_name;
        this.editGroup.teacher_id = group.teacher_id;
      }

      this.showEditGroupForm = true;
    },
    
    removeStudent(studentId) {
      this.newGroup.students = this.newGroup.students.filter(student => student.id !== studentId);
    },
    
    removeStudentFromEdit(studentId) {
      this.editGroup.students = this.editGroup.students.filter(student => student.id !== studentId);
    },
    
    closeAddGroupForm() {
      this.showAddGroupForm = false;
      this.resetNewGroup();
    },
    
    closeEditGroupForm() {
      this.showEditGroupForm = false;
      this.resetEditGroup();
    },
    
    async fetchTeachers(isEditing = false) {
      const name = isEditing ? this.editGroup.teacherName : this.newGroup.teacherName;

      if (name.length >= 3) {
        try {
          const response = await axiosInstance.get('/admin/teachers', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            params: { name }
          });

          const suggestions = response.data.map(teacher => ({
            id: teacher.id,
            name: `${teacher.first_name} ${teacher.last_name}`
          }));

          this.teacherSuggestions = suggestions;
          this.teacherError = this.teacherSuggestions.length === 0;
        } catch (error) {
          console.error('Error fetching teachers:', error);
          this.teacherSuggestions = [];
          this.teacherError = true;
        }
      } else {
        this.teacherSuggestions = [];
        this.teacherError = false;
      }
    },
    
    selectTeacher(teacher, isEditing = false) {
      if (isEditing) {
        this.editGroup.teacherName = teacher.name;
        this.editGroup.teacher_id = teacher.id;
      } else {
        this.newGroup.teacherName = teacher.name;
        this.newGroup.teacher_id = teacher.id;
      }
      this.teacherError = false;
      this.teacherSuggestions = [];
    },
    
    async fetchStudents(isEditing = false) {
      const name = isEditing ? this.editStudentName : this.newStudentName;

      if (name.length >= 3) {
        try {
          const response = await axiosInstance.get('/admin/students', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            params: { name }
          });

          const suggestions = response.data.map(student => ({
            id: student.id,
            name: `${student.first_name} ${student.last_name}`
          }));

          this.studentSuggestions = suggestions;
          this.studentError = this.studentSuggestions.length === 0;
        } catch (error) {
          console.error('Error fetching students:', error);
          this.studentSuggestions = [];
          this.studentError = true;
        }
      } else {
        this.studentSuggestions = [];
        this.studentError = false;
      }
    },
    
    selectStudent(student, isEditing = false) {
      if (isEditing) {
        this.editStudentName = student.name;
        this.selectedEditStudent = student;
      } else {
        this.newStudentName = student.name;
        this.selectedNewStudent = student;
      }
      this.studentError = false;
      this.studentSuggestions = [];
    },
    
    addStudent() {
      if (this.selectedNewStudent) {
        // Check if student already added
        const alreadyAdded = this.newGroup.students.some(s => s.id === this.selectedNewStudent.id);
        
        if (!alreadyAdded) {
          this.newGroup.students.push(this.selectedNewStudent);
        } else {
          this.showNotification('error', this.$t('groupsManagement.studentAlreadyAdded'));
        }
        
        this.newStudentName = '';
        this.selectedNewStudent = null;
      }
    },
    
    addStudentToEdit() {
      if (this.selectedEditStudent) {
        // Check if student already added
        const alreadyAdded = this.editGroup.students.some(s => s.id === this.selectedEditStudent.id);
        
        if (!alreadyAdded) {
          this.editGroup.students.push(this.selectedEditStudent);
        } else {
          this.showNotification('error', this.$t('groupsManagement.studentAlreadyAdded'));
        }
        
        this.editStudentName = '';
        this.selectedEditStudent = null;
      }
    },
    
    showNotification(type, message) {
      this.notificationType = type;  // Store the notification type
      this.notificationMessage = message;  // Store the notification message
      this.notificationVisible = true;
      
      // Auto hide after 3 seconds
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    }
  },
  created() {
    this.searchGroups();
  }
};
</script>

<style scoped>
/* Base layout */
.groups-management-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  color: #333;
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

/* Search container */
.search-container {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 16px;
  pointer-events: none;
}

.search-input {
  flex: 1;
  padding: 10px 12px 10px 40px;
  height: 42px;
  border-radius: 21px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.btn-search {
  margin-left: 10px;
  padding: 8px 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 21px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-search:hover {
  background-color: #2563eb;
}

/* Table styling */
.table-container {
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
}

.data-table th {
  padding: 16px 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.data-table th:hover {
  background-color: #e9ecef;
}

.sort-icon {
  margin-left: 8px;
  opacity: 0.5;
}

.sort-icon.active {
  opacity: 1;
}

.data-table tbody tr {
  transition: background-color 0.2s;
  border-bottom: 1px solid #edf2f7;
}

.data-table tbody tr:hover {
  background-color: #f1f5f9;
}

.data-table td {
  padding: 14px 12px;
  vertical-align: middle;
}

/* Student Lists in Table */
.students-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 100px;
  overflow-y: auto;
}

.student-item {
  padding: 0.25rem 0;
  color: #475569;
  display: flex;
  align-items: center;
}

.student-item::before {
  content: '•';
  color: #3b82f6;
  margin-right: 0.5rem;
  font-weight: bold;
}

.no-students {
  font-style: italic;
  color: #94a3b8;
}

/* Action buttons */
.actions-cell {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.btn-icon {
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  cursor: pointer;
  border: 1px solid;
}

.btn-icon.edit {
  background-color: #fff;
  color: #3b82f6;
  border-color: #3b82f6;
}

.btn-icon.edit:hover {
  background-color: #3b82f6;
  color: #fff;
}

.btn-icon.delete {
  background-color: #fff;
  color: #dc2626;
  border-color: #dc2626;
}

.btn-icon.delete:hover {
  background-color: #dc2626;
  color: #fff;
}

/* Empty state */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 3.5rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state p {
  font-size: 1.1rem;
}

/* Pagination styles */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  flex-wrap: wrap;
  gap: 16px;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-selector label {
  color: #4b5563;
  font-weight: 500;
  white-space: nowrap;
}

.page-size-selector select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #4b5563;
  cursor: pointer;
  min-width: 70px;
}

.page-size-selector select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
}

.pagination-info {
  color: #4b5563;
  font-size: 0.9rem;
}

/* Update the existing pagination-nav class */
.pagination-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-status {
  padding: 6px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  color: #495057;
  font-weight: 500;
}

.btn, .btn-primary {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover {
  background-color: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.3);
}

.btn-secondary {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.25);
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(239, 68, 68, 0.3);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 10px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  color: #212529;
  font-weight: 600;
  font-size: 1.5rem;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
  padding: 4px;
}

.close-button:hover {
  color: #1f2937;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #4b5563;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

/* Selected items in form */
.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.selected-item {
  display: flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.9rem;
}

.remove-button {
  background: none;
  border: none;
  color: #64748b;
  margin-left: 6px;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-button:hover {
  color: #dc2626;
}

/* Student search */
.search-input-wrapper {
  position: relative;
  margin-bottom: 12px;
}

.add-student-input {
  display: flex;
  gap: 8px;
}

.dropdown-suggestions {
  position: absolute;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.suggestion-item {
  padding: 10px 12px;
  line-height: 1.5;
  white-space: normal;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background: #f8fafc;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 4px;
}

/* Confirmation modal */
.confirmation-modal {
  max-width: 450px;
}

.confirmation-content {
  margin: 24px 0;
}

.warning-text {
  color: #f59e0b;
  font-size: 0.9rem;
  margin-top: 8px;
}

/* Toast notification */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background-color: white;
  border-left: 4px solid #10b981;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  min-width: 300px;
  z-index: 1050;
  animation: slideInRight 0.3s, fadeOut 0.3s 2.7s forwards;
  transform: translateX(0);
}

.toast-notification.error {
  border-left-color: #dc2626;
}

.toast-notification.error .toast-icon {
  color: #dc2626;
}

.toast-notification.success {
  border-left-color: #10b981;
}

.toast-notification.success .toast-icon {
  color: #10b981;
}

.toast-icon {
  font-size: 1.25rem;
  margin-right: 12px;
}

.toast-content {
  font-size: 0.95rem;
  color: #1f2937;
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    margin-bottom: 8px;
  }
  
  .btn-search {
    margin-left: 0;
    width: 100%;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-footer button {
    width: 100%;
  }
}
</style>